"use client"

// src/pages/LandingPage.js
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { FaCrown, FaBolt, FaLock, FaChartLine, FaUsers, FaGlobe, FaArrowRight, FaStar } from "react-icons/fa"
import { useContext } from "react"
import { AuthContext } from "../context/AuthContext"
import NavBar from "../components/Layout/Navbar"
import Footer from "../components/Layout/Footer"
import PricingCard from "../components/payment/PricingCard"
import api from "../services/api"
import PageMeta from "../components/pagesHelmet/PageMeta"

export default function LandingPage() {
  const [plans, setPlans] = useState([])
  const { user, loading } = useContext(AuthContext)

  // const [loading, setLoading] = useState(true)

  const testimonials = [
    {
      name: "Jane Doe",
      role: "Digital Marketer",
      message: "u2lr transformed how I track links for my campaigns. Highly recommend!",
    },
    {
      name: "John Smith",
      role: "Startup Founder",
      message: "Premium features like custom domains and analytics are game-changers.",
    },
    {
      name: "Sarah Lee",
      role: "Freelancer",
      message: "Simple, fast, and secure. The best link shortening platform I've used.",
    },
  ]

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get("/subscription/plans")
        // Map API response to array suitable for PricingCard
        const mappedPlans = Object.entries(res.data).map(([key, plan]) => ({
          key,
          title: plan.title,
          price: plan.pricing,
          features: plan.features,
          limit: plan.limit,
          monthlyUrls: plan.monthlyUrls,
          trafficLimit: plan.trafficLimit,
          highlight: ["premium", "dedicated"].includes(key),
        }))
        setPlans(mappedPlans)
      } catch (err) {
        console.error("Failed to fetch plans:", err)
      } finally {
        // setLoading(false)
      }
    }
    fetchPlans()
  }, [])

  return (
    <>
      <style>
        {`
          /* Updated to use brand color palette */
          :root {
            --primary: #4F46E5;
            --primary-dark: #3730A3;
            --accent: #22C55E;
            --warning: #F59E0B;
            --danger: #EF4444;
            --background: #0F172A;
            --surface: #111827;
            --text: #E5E7EB;
            --primary-gradient: linear-gradient(135deg, #4F46E5 0%, #3730A3 100%);
            --text-dark: #1F2937;
            --text-light: #6B7280;
          }

          /* Smooth scroll behavior */
          html {
            scroll-behavior: smooth;
          }

          /* Updated hero section with brand background and animations */
          .hero-section {
            background: linear-gradient(135deg, var(--background) 0%, var(--surface) 100%);
            min-height: 90vh;
            position: relative;
            overflow: hidden;
            display: flex;
            align-items: center;
          }

          .hero-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: 
              radial-gradient(circle at 20% 50%, rgba(79, 70, 229, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(79, 70, 229, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 50% 0%, rgba(34, 197, 94, 0.05) 0%, transparent 60%);
            animation: gradientShift 15s ease-in-out infinite;
          }

          @keyframes gradientShift {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.8; }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideInDown {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          .hero-content {
            position: relative;
            z-index: 2;
          }

          /* Updated gradient text with primary color */
          .gradient-text {
            background: var(--primary-gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .feature-card {
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            border: 1px solid rgba(255, 255, 255, 0.1);
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            height: 100%;
            border-radius: 20px;
            overflow: hidden;
            position: relative;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          }

          .feature-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: var(--primary-gradient);
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .feature-card:hover::before {
            transform: scaleX(1);
          }

          .feature-card:hover {
            transform: translateY(-12px);
            box-shadow: 0 25px 50px rgba(79, 70, 229, 0.2);
            border-color: var(--primary);
            background: rgba(255, 255, 255, 0.98);
          }

          /* Updated feature card accent with primary color */
          .feature-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: var(--primary-gradient);
            transform: scaleX(0);
            transition: transform 0.4s ease;
          }

          .feature-card:hover::before {
            transform: scaleX(1);
          }

          .feature-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 40px rgba(79, 70, 229, 0.15);
            border-color: var(--primary);
          }

          .feature-icon {
            width: 64px;
            height: 64px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 16px;
            margin: 0 auto 1.5rem;
            font-size: 1.75rem;
            transition: all 0.3s ease;
          }

          .feature-card:hover .feature-icon {
            transform: scale(1.1) rotate(5deg);
          }

          /* Updated icon colors to use brand colors */
          .icon-primary { 
            background: var(--primary-gradient); 
            color: white; 
          }
          .icon-accent { 
            background: linear-gradient(135deg, #22C55E 0%, #16A34A 100%); 
            color: white; 
          }
          .icon-warning { 
            background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%); 
            color: white; 
          }
          .icon-info { 
            background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%); 
            color: white; 
          }

          .stat-card {
            padding: 2.5rem 2rem;
            background: rgba(255, 255, 255, 0.97);
            border-radius: 20px;
            box-shadow: 0 8px 30px rgba(0,0,0,0.08);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            backdrop-filter: blur(5px);
          }

          .stat-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 50px rgba(79, 70, 229, 0.18);
            border-color: rgba(79, 70, 229, 0.3);
            background: rgba(255, 255, 255, 0.99);
          }

          /* Updated stat card hover with primary color */
          .stat-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 35px rgba(79, 70, 229, 0.15);
            border-color: var(--primary);
          }

          .testimonial-card {
            background: rgba(255, 255, 255, 0.96);
            border-radius: 20px;
            padding: 2.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.08);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            height: 100%;
            border: 1px solid rgba(255, 255, 255, 0.2);
            position: relative;
            overflow: hidden;
            backdrop-filter: blur(10px);
          }

          .testimonial-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 3px;
            height: 100%;
            background: var(--primary-gradient);
            transform: scaleY(0);
            transform-origin: top;
            transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .testimonial-card:hover::before {
            transform: scaleY(1);
          }

          .testimonial-card:hover {
            transform: translateY(-10px);
            box-shadow: 0 20px 60px rgba(79, 70, 229, 0.2);
            border-color: rgba(79, 70, 229, 0.3);
            background: rgba(255, 255, 255, 0.98);
          }

          /* Updated testimonial card accent with primary color */
          .testimonial-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 4px;
            height: 100%;
            background: var(--primary-gradient);
            transform: scaleY(0);
            transition: transform 0.4s ease;
          }

          .testimonial-card:hover::before {
            transform: scaleY(1);
          }

          .testimonial-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 16px 50px rgba(79, 70, 229, 0.15);
            border-color: var(--primary);
          }

          /* Updated button styles with primary brand color */
          .btn-gradient {
            background: var(--primary-gradient);
            border: none;
            color: white;
            padding: 0.95rem 2.8rem;
            border-radius: 50px;
            font-weight: 700;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 8px 20px rgba(79, 70, 229, 0.3);
            position: relative;
            overflow: hidden;
            letter-spacing: 0.3px;
          }

          .btn-gradient::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
            transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .btn-gradient:hover::before {
            left: 100%;
          }

          .btn-gradient:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 35px rgba(79, 70, 229, 0.45);
            color: white;
          }

          .btn-gradient:active {
            transform: translateY(-1px);
          }

          .btn-outline-gradient {
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.8);
            color: white;
            padding: 0.95rem 2.8rem;
            border-radius: 50px;
            font-weight: 700;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            backdrop-filter: blur(5px);
            letter-spacing: 0.3px;
          }

          .btn-outline-gradient:hover {
            background: rgba(255, 255, 255, 0.95);
            color: var(--primary);
            border-color: white;
            transform: translateY(-3px);
            box-shadow: 0 12px 30px rgba(255, 255, 255, 0.2);
          }

          .section-title {
            font-size: 2.5rem;
            font-weight: 800;
            margin-bottom: 1rem;
            color: var(--text-dark);
            position: relative;
            display: inline-block;
          }

          .section-subtitle {
            font-size: 1.125rem;
            color: var(--text-light);
            max-width: 600px;
            margin: 0 auto 3rem;
            line-height: 1.8;
          }

          /* Updated CTA section with brand background */
          .cta-section {
            background: linear-gradient(135deg, var(--background) 0%, var(--surface) 100%);
            position: relative;
            overflow: hidden;
          }

          .cta-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%234F46E5' fillOpacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          }

          /* Added badge styling with primary color theme */
          .hero-badge {
            background: rgba(79, 70, 229, 0.25);
            backdrop-filter: blur(15px);
            border: 1px solid rgba(79, 70, 229, 0.4);
            transition: all 0.3s ease;
            animation: float 3s ease-in-out infinite;
          }

          .hero-badge:hover {
            background: rgba(79, 70, 229, 0.35);
            border-color: rgba(79, 70, 229, 0.6);
            transform: translateY(-2px);
          }

          /* Responsive optimizations */
          @media (max-width: 768px) {
            .hero-section {
              min-height: 85vh;
            }

            .hero-content h1 {
              font-size: 2rem !important;
            }

            .section-title {
              font-size: 1.8rem;
            }

            .btn-gradient, .btn-outline-gradient {
              padding: 0.85rem 2rem;
              font-size: 0.95rem;
            }

            .stat-card {
              padding: 1.5rem 1rem;
            }
          }

          @media (max-width: 480px) {
            .hero-content {
              padding: 0 1rem;
            }

            .hero-content h1 {
              font-size: 1.5rem !important;
              line-height: 1.3;
            }

            .hero-content p {
              font-size: 1rem;
            }

            .section-title {
              font-size: 1.5rem;
            }

            .row.g-4 {
              gap: 1rem !important;
            }

            .feature-card, .stat-card, .testimonial-card {
              padding: 1.5rem;
              border-radius: 16px;
            }
          }

          /* Updated stat number styling with primary gradient */
          .stat-number {
            font-size: 2.5rem;
            font-weight: 800;
            background: var(--primary-gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          /* Added accent color utility classes */
          .text-accent { color: var(--accent); }
          .text-warning { color: var(--warning); }
          .text-primary { color: var(--primary); }
        `}
      </style>
          <PageMeta title="Home | U2LR URL Shortener" description="" />      <NavBar />

      <section className="hero-section">
        <div className="container hero-content">
          <div className="row min-vh-90 align-items-center">
            <div className="col-lg-8 mx-auto text-center text-white py-5">
              <div className="hero-badge text-white px-4 py-2 rounded-pill mb-4 fw-semibold">
                Trusted by 50,000+ Professionals Worldwide
              </div>
              <h1 className="display-3 fw-bold mb-4" style={{ lineHeight: "1.2" }}>
                Short Links,
                <br />
                Smarter Analytics
              </h1>
              <p className="lead mb-5 fs-4 opacity-90">
                Create custom links, track performance, and unlock premium features
                <br />
                that scale with your growth.
              </p>
<div className="d-flex gap-3 justify-content-center flex-wrap">
  {!loading && !user && (
    <>
      <Link to="/register" className="btn btn-gradient btn-lg">
        Get Started Free <FaArrowRight className="ms-2" />
      </Link>
      <Link to="/login" className="btn btn-outline-gradient btn-lg">
        Login
      </Link>
    </>
  )}

  {!loading && user && (
    <>
      <Link to="/user-dashboard" className="btn btn-gradient btn-lg">
        Go to Dashboard <FaArrowRight className="ms-2" />
      </Link>

      {user.plan !== "premium" && user.plan !== "dedicated" && (
        <Link to="/account/upgrade" className="btn btn-outline-gradient btn-lg">
          Upgrade Plan
        </Link>
      )}
    </>
  )}
</div>

            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container py-4">
          <div className="row g-4">
            <div className="col-md-3 col-sm-6">
              <div className="stat-card text-center">
                <FaUsers className="mb-3" style={{ fontSize: "2.5rem", color: "var(--primary)" }} />
                <h3 className="stat-number mb-2">50K+</h3>
                <p className="text-muted mb-0 fw-medium">Active Users</p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="stat-card text-center">
                <FaGlobe className="mb-3" style={{ fontSize: "2.5rem", color: "var(--accent)" }} />
                <h3 className="stat-number mb-2">100+</h3>
                <p className="text-muted mb-0 fw-medium">Countries Served</p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="stat-card text-center">
                <FaBolt className="mb-3" style={{ fontSize: "2.5rem", color: "var(--warning)" }} />
                <h3 className="stat-number mb-2">99.9%</h3>
                <p className="text-muted mb-0 fw-medium">Uptime Guarantee</p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="stat-card text-center">
                <FaChartLine className="mb-3" style={{ fontSize: "2.5rem", color: "var(--primary)" }} />
                <h3 className="stat-number mb-2">1M+</h3>
                <p className="text-muted mb-0 fw-medium">Links Tracked</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-white">
  <div className="container py-5">
    <div className="row align-items-center g-5">
      <div className="col-lg-6">
        <h2 className="section-title mb-4">About u2lr</h2>
        <p className="text-muted fs-5 mb-4" style={{ lineHeight: "1.8" }}>
          u2lr is a modern link management platform built for professionals who
          need more than basic link shortening. We help individuals, startups,
          and growing teams create branded links, track performance, and make
          data-driven decisions with confidence.
        </p>
        <p className="text-muted fs-6" style={{ lineHeight: "1.8" }}>
          Our platform is designed with performance, security, and scalability
          at its core—offering fast redirects, real-time analytics, and flexible
          upgrade paths as your needs grow. We focus on transparency, reliability,
          and continuous improvement rather than inflated claims.
        </p>
      </div>

      <div className="col-lg-6">
        <div className="row g-4">
          <div className="col-sm-6">
            <div className="stat-card text-center">
              <h3 className="stat-number mb-2">99.9%</h3>
              <p className="text-muted mb-0 fw-medium">Platform Uptime</p>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="stat-card text-center">
              <h3 className="stat-number mb-2">1M+</h3>
              <p className="text-muted mb-0 fw-medium">Links Processed</p>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="stat-card text-center">
              <h3 className="stat-number mb-2">100+</h3>
              <p className="text-muted mb-0 fw-medium">Countries Reached</p>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="stat-card text-center">
              <h3 className="stat-number mb-2">24/7</h3>
              <p className="text-muted mb-0 fw-medium">System Monitoring</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>


      <section className="py-5 bg-white">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="section-title">Why Choose u2lr?</h2>
            <p className="section-subtitle">
              Everything you need to create, manage, and analyze your links with enterprise-grade features.
            </p>
          </div>
          <div className="row g-4">
            <div className="col-lg-3 col-md-6">
              <div className="card feature-card p-4">
                <div className="feature-icon icon-primary">
                  <FaCrown />
                </div>
                <h5 className="fw-bold mb-3 text-center">Premium Features</h5>
                <p className="text-muted text-center mb-0">
                  Custom domains, API access, analytics, unlimited links, and priority support.
                </p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card feature-card p-4">
                <div className="feature-icon icon-warning">
                  <FaBolt />
                </div>
                <h5 className="fw-bold mb-3 text-center">Lightning Fast</h5>
                <p className="text-muted text-center mb-0">
                  99.9% uptime with blazing-fast redirects for users around the globe.
                </p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card feature-card p-4">
                <div className="feature-icon icon-accent">
                  <FaLock />
                </div>
                <h5 className="fw-bold mb-3 text-center">Secure & Safe</h5>
                <p className="text-muted text-center mb-0">
                  Password protection, HTTPS encryption, and advanced anti-spam security.
                </p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card feature-card p-4">
                <div className="feature-icon icon-info">
                  <FaChartLine />
                </div>
                <h5 className="fw-bold mb-3 text-center">Advanced Analytics</h5>
                <p className="text-muted text-center mb-0">
                  Track clicks, devices, locations, and conversion trends in real-time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-5 bg-light">
  <div className="container py-5">
    <div className="text-center mb-5">
      <h2 className="section-title">Platform Readiness & Trust</h2>
      <p className="section-subtitle">
        Built with modern standards, transparent capabilities, and a clear growth roadmap.
      </p>
    </div>

    <div className="row g-4">
      {/* Trust & Business Readiness */}
      <div className="col-lg-4 col-md-6">
        <div className="card feature-card p-4 h-100">
          <div className="feature-icon icon-accent">
            <FaLock />
          </div>
          <h5 className="fw-bold text-center mb-3">
            Trust & Business Readiness
          </h5>
          <p className="text-muted text-center mb-0" style={{ lineHeight: "1.7" }}>
            u2lr already delivers secure links, HTTPS encryption, uptime reliability,
            and transparent pricing. Additional trust layers such as extended legal
            documentation, compliance badges, and public uptime reporting are actively
            being rolled out.
          </p>
        </div>
      </div>

      {/* Conversion Depth */}
      <div className="col-lg-4 col-md-6">
        <div className="card feature-card p-4 h-100">
          <div className="feature-icon icon-primary">
            <FaChartLine />
          </div>
          <h5 className="fw-bold text-center mb-3">
            Conversion Depth
          </h5>
          <p className="text-muted text-center mb-0" style={{ lineHeight: "1.7" }}>
            Strong core conversion flows are already in place, including fast onboarding,
            intuitive dashboards, and clear upgrade paths. Upcoming improvements focus on
            deeper funnel analytics, smarter prompts, and higher-intent conversion triggers.
          </p>
        </div>
      </div>

      {/* Enterprise Readiness */}
      <div className="col-lg-4 col-md-6">
        <div className="card feature-card p-4 h-100">
          <div className="feature-icon icon-warning">
            <FaGlobe />
          </div>
          <h5 className="fw-bold text-center mb-3">
            Enterprise Readiness
          </h5>
          <p className="text-muted text-center mb-0" style={{ lineHeight: "1.7" }}>
            While u2lr is currently optimized for individuals, startups, and growing teams,
            enterprise-grade features such as SLAs, advanced access controls, and dedicated
            account management are planned as part of the platform’s expansion roadmap.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<section className="py-5 bg-light">
  <div className="container py-5">
    <div className="text-center mb-5">
      <h2 className="section-title">Trusted Technology & Ecosystem</h2>
      <p className="section-subtitle">
        Built using proven infrastructure and industry-standard services trusted
        by millions of businesses worldwide.
      </p>
    </div>

    <div className="row g-4 justify-content-center">
      <div className="col-lg-3 col-md-4 col-sm-6">
        <div className="card feature-card p-4 text-center h-100">
          <div className="feature-icon icon-primary">
            <FaGlobe />
          </div>
          <h6 className="fw-bold mb-2">Global Infrastructure</h6>
          <p className="text-muted mb-0">
            Hosted on scalable, high-availability cloud infrastructure for
            fast global redirects.
          </p>
        </div>
      </div>

      <div className="col-lg-3 col-md-4 col-sm-6">
        <div className="card feature-card p-4 text-center h-100">
          <div className="feature-icon icon-accent">
            <FaLock />
          </div>
          <h6 className="fw-bold mb-2">Secure by Design</h6>
          <p className="text-muted mb-0">
            HTTPS-only links, encrypted data handling, and continuous security
            monitoring.
          </p>
        </div>
      </div>

      <div className="col-lg-3 col-md-4 col-sm-6">
        <div className="card feature-card p-4 text-center h-100">
          <div className="feature-icon icon-warning">
            <FaBolt />
          </div>
          <h6 className="fw-bold mb-2">Performance Optimized</h6>
          <p className="text-muted mb-0">
            Low-latency redirects and optimized caching for high-volume traffic.
          </p>
        </div>
      </div>

      <div className="col-lg-3 col-md-4 col-sm-6">
        <div className="card feature-card p-4 text-center h-100">
          <div className="feature-icon icon-info">
            <FaChartLine />
          </div>
          <h6 className="fw-bold mb-2">Analytics Stack</h6>
          <p className="text-muted mb-0">
            Data pipelines designed for accurate, real-time link performance
            insights.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>



      <section className="py-5 bg-light">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="section-title">What Our Users Say</h2>
            <p className="section-subtitle">
              Join thousands of satisfied customers who trust u2lr for their link management.
            </p>
          </div>
          <div className="row g-4">
            {testimonials.map((t, idx) => (
              <div key={idx} className="col-md-4">
                <div className="card testimonial-card">
                  <div className="d-flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} style={{ color: "var(--warning)" }} />
                    ))}
                  </div>
                  <p className="text-muted mb-4" style={{ fontSize: "1.05rem", lineHeight: "1.7" }}>
                    "{t.message}"
                  </p>
                  <div className="mt-auto">
                    <h6 className="fw-bold mb-1">{t.userName}</h6>
                    <small className="fw-semibold" style={{ color: "var(--primary)" }}>
                      {t.role}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5 bg-white">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-subtitle">
              Choose the perfect plan for your needs. Upgrade or downgrade at any time.
            </p>
          </div>
          <div className="row g-4 justify-content-center">
            {plans.map((plan) => (
              <div key={plan.key} className="col-lg-4 col-md-6">
                <PricingCard
                  title={plan.title}
                  price={plan.price}
                  billingType="monthly"
                  limit={plan.limit}
                  monthlyUrls={plan.monthlyUrls}
                  trafficLimit={plan.trafficLimit}
                  highlight={plan.highlight}
                  button={() => (window.location.href = "/account/upgrade")}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

<section className="cta-section py-5 text-white position-relative">
  <div className="container py-5 text-center position-relative" style={{ zIndex: 2 }}>
    <h2 className="display-5 fw-bold mb-4">Ready to Transform Your Links?</h2>
    <p className="lead mb-5 fs-5">
      Join thousands of professionals using u2lr for smarter link management and analytics.
    </p>

    {!loading && !user && (
      <Link
        to="/register"
        className="btn btn-light btn-lg px-5 py-3 rounded-pill fw-semibold shadow-lg"
      >
        Get Started
      </Link>
    )}

    {!loading && user && (
      <Link
        to="/user-dashboard"
        className="btn btn-light btn-lg px-5 py-3 rounded-pill fw-semibold shadow-lg"
      >
        Get Started Now <FaArrowRight className="ms-2" />
      </Link>
    )}
  </div>
</section>

      <Footer />
    </>
  )
}
